// Background videos marked [data-twm-lazy-video] fetch nothing until they near the viewport,
// then play while visible. Reduced-motion and Save-Data visitors keep the poster frame.
// Posters go in data-poster: browsers fetch a poster attribute at once, even far below
// the fold, which takes bandwidth from the hero image on slow connections.
//
// Safari refuses muted autoplay in Low Power Mode (iPhone, Mac) and when a site is set to
// "Never Auto-Play"; play() then rejects with NotAllowedError. Those videos are retried on
// the visitor's first tap, click or key press (Safari counts that as permission) and when
// the tab becomes visible again, instead of staying on the poster for good.
(() => {
  // Loaded by more than one section: run once per page.
  if (window.__twmLazyVideo) return;
  window.__twmLazyVideo = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection && navigator.connection.saveData;
  const blocked = new Set();
  const visible = new Set();

  const prepare = (video) => {
    // Safari reads these properties, not only the attributes, when deciding on autoplay.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  };

  const showPoster = (video) => {
    if (video.dataset.poster && !video.getAttribute('poster')) video.poster = video.dataset.poster;
  };

  const load = (video) => {
    showPoster(video);
    if (video.dataset.twmLazyLoaded) return;
    prepare(video);
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });
    video.load();
    video.dataset.twmLazyLoaded = 'true';
  };

  const play = (video) => {
    load(video);
    prepare(video);
    const attempt = video.play();
    if (!attempt) return;
    attempt
      .then(() => blocked.delete(video))
      .catch((error) => {
        if (error && error.name === 'NotAllowedError') blocked.add(video);
      });
  };

  const retryBlocked = () => {
    blocked.forEach((video) => {
      if (visible.has(video)) play(video);
    });
  };

  ['pointerdown', 'touchend', 'keydown', 'click'].forEach((type) => {
    window.addEventListener(type, retryBlocked, { passive: true });
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') visible.forEach(play);
  });

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach(({ isIntersecting, target }) => {
              if (isIntersecting) {
                showPoster(target);
                if (reduceMotion || saveData) return;
                visible.add(target);
                play(target);
              } else {
                visible.delete(target);
                if (target.dataset.twmLazyLoaded) target.pause();
              }
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  const init = (root = document) => {
    const videos = root.querySelectorAll('video[data-twm-lazy-video]:not([data-twm-lazy-init])');
    videos.forEach((video) => {
      video.dataset.twmLazyInit = 'true';
      if (observer) observer.observe(video);
      else if (reduceMotion || saveData) showPoster(video);
      else {
        visible.add(video);
        play(video);
      }
    });
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
